import { normalizeFinding, validateFindingDetails, validateFinding } from "./findingFormRules";
import DeficiencyFields from "./DeficiencyFields";
import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalBody } from "../Common/AppOverlay";
import "./FindingEditorModal.scss";

const fallbackCategories = [
  "Documentation",
  "Independence",
  "Quality Management",
  "Engagement Performance",
  "Professional Standards",
  "Other",
];
const fallbackSeverities = [
  { name: "Critical" },
  { name: "High" },
  { name: "Medium" },
  { name: "Low" },
];
const responseRoles = [
  "Engagement Partner",
  "Engagement Manager",
  "Quality Manager",
  "Preparer",
  "Client Contact",
  "Firm Administrator",
];

const fileIcon = (fileName = "") => {
  const extension = fileName.split(".").pop()?.toLowerCase();
  if (extension === "pdf") return "bx-file-pdf";
  if (["xls", "xlsx", "csv"].includes(extension)) return "bx-spreadsheet";
  if (["ppt", "pptx"].includes(extension)) return "bx-slideshow";
  return "bx-file";
};

const FindingEditorModal = ({
  mode,
  findingNumber,
  engagement,
  checklist,
  question,
  value,
  onChange,
  configuration,
  requiredFields,
  evidenceFiles,
  assignees,
  errors,
  onErrors,
  onBrowseFiles,
  onRemoveEvidence,
  onClose,
  onSave,
  saving = false,
  evidenceUploading = false,
}) => {
  const [step, setStep] = useState(1);
  useEffect(() => {
    setStep(1);
  }, [mode, findingNumber]);

  const categories = configuration?.categories?.length
    ? configuration.categories
    : fallbackCategories;
  const severities = configuration?.severityLevels?.length
    ? configuration.severityLevels
    : fallbackSeverities;
  const linkedEvidenceFiles = evidenceFiles.filter((file) =>
    value.evidenceIds?.some((id) => String(id) === String(file.id))
  );
  const hasDeficiency = value.deficiencyExists === true;
  const actionRequired = value.managementResponseRequired === "yes";

  const update = (changes) => {
    onErrors({});
    onChange((current) => normalizeFinding({ ...current, ...changes }));
    if (changes.deficiencyExists === false) setStep(1);
  };
  const normalizedRequired = useMemo(
    () => (requiredFields instanceof Set ? requiredFields : new Set()),
    [requiredFields]
  );

  const continueToAssignment = () => {
    const nextErrors = validateFindingDetails(value, normalizedRequired);
    onErrors(nextErrors);
    if (!Object.keys(nextErrors).length) {
      if (hasDeficiency) setStep(2);
      else onSave();
    }
  };

  const cannotSave = Object.keys(validateFinding(normalizeFinding(value), normalizedRequired)).length > 0;
  const save = () => {
    const nextErrors = validateFinding(normalizeFinding(value), normalizedRequired);
    onErrors(nextErrors);
    if (!Object.keys(nextErrors).length && !saving && !evidenceUploading) onSave();
  };

  if (!mode) return null;

  return (
    <Modal
      isOpen
      centered
      size="xl"
      toggle={() => !saving && onClose()}
      className="finding-editor-modal"
    >
      <header className="finding-editor__header">
        <div>
          <i className="bx bx-flag" />
          <span>
            <h2>
              {mode === "edit"
                ? `Edit Finding · ${findingNumber || ""}`
                : "New Finding"}
            </h2>
            <p>
              {step === 1
                ? "Record a finding for this inspection"
                : "Define the resolution route and owner"}
            </p>
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          aria-label="Close finding"
        >
          <i className="bx bx-x" />
        </button>
      </header>

      {hasDeficiency && (
        <nav
          className="finding-editor__steps"
          aria-label="Finding creation progress"
        >
          <button
            type="button"
            className={step === 1 ? "active" : "complete"}
            onClick={() => setStep(1)}
          >
            <span>1</span>
            <b>Finding details</b>
            <small>Initial entry</small>
          </button>
          <i />
          <button
            type="button"
            className={step === 2 ? "active" : ""}
            disabled={step === 1}
            onClick={() => setStep(2)}
          >
            <span>2</span>
            <b>Resolution &amp; assignment</b>
            <small>Action required</small>
          </button>
        </nav>
      )}

      <section className="finding-editor__context" aria-label="Finding context">
        <div>
          <i className="bx bx-briefcase" />
          <span>
            <small>Engagement</small>
            <b>{engagement}</b>
          </span>
        </div>
        <div>
          <i className="bx bx-list-check" />
          <span>
            <small>Checklist</small>
            <b>{checklist}</b>
          </span>
        </div>
        <div>
          <i className="bx bx-question-mark" />
          <span>
            <small>Question</small>
            <b>{question}</b>
          </span>
        </div>
      </section>

      <ModalBody className="finding-editor__body">
        {errors.submission && <p role="alert">{errors.submission}</p>}
        <DeficiencyFields
          selectorOnly
          value={value}
          update={update}
          errors={errors}
        />
        {value.aiActionId && (
          <p role="status">AI generated - review before creating.</p>
        )}
        {step === 1 ? (
          <section className="finding-editor__grid">
            <label className="is-wide">
              <b>
                Title <em>*</em>
              </b>
              <input
                maxLength="200"
                value={value.title}
                onChange={(event) => update({ title: event.target.value })}
                placeholder="Enter a concise finding title"
              />
              <small className="finding-editor__counter">
                {value.title.length}/200
              </small>
              {errors.title && (
                <small className="finding-editor__error">{errors.title}</small>
              )}
            </label>
            <label>
              <b>
                Category <em>*</em>
              </b>
              <select
                value={value.category}
                onChange={(event) => update({ category: event.target.value })}
              >
                <option value="">Select category</option>
                {categories.map((item) => {
                  const name = item.name || item;
                  return (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  );
                })}
              </select>
              {errors.category && (
                <small className="finding-editor__error">
                  {errors.category}
                </small>
              )}
            </label>
            <label className="is-wide">
              <b>
                Description <em>*</em>
              </b>
              <textarea
                rows="4"
                maxLength="2000"
                value={value.description}
                onChange={(event) =>
                  update({ description: event.target.value })
                }
                placeholder="Describe what was observed, the impact, and the relevant standard."
              />
              <small className="finding-editor__counter">
                {value.description.length}/2000
              </small>
              {errors.description && (
                <small className="finding-editor__error">
                  {errors.description}
                </small>
              )}
            </label>
            {hasDeficiency && (
              <DeficiencyFields
                hideSelector
                value={value}
                update={update}
                errors={errors}
                severityControl={
                  <fieldset className="finding-editor__severity">
                    <legend>
                      Severity <em>*</em>
                    </legend>
                    <div>
                      {severities.map((item) => {
                        const name = item.name || item;
                        return (
                          <button
                            type="button"
                            key={name}
                            className={`is-${String(name).toLowerCase()} ${
                              value.severity === name ? "active" : ""
                            }`}
                            onClick={() => update({ severity: name })}
                          >
                            <i /> {name}
                          </button>
                        );
                      })}
                    </div>
                    {errors.severity && (
                      <small className="finding-editor__error">
                        {errors.severity}
                      </small>
                    )}
                  </fieldset>
                }
              />
            )}
            {hasDeficiency && (
              <>
                <fieldset className="finding-editor__evidence is-wide">
                  <legend>
                    Supporting evidence
                    {normalizedRequired.has("supportingevidence") && (
                      <em> *</em>
                    )}
                  </legend>
                  <button
                    type="button"
                    className="finding-editor__upload"
                    onClick={onBrowseFiles}
                    disabled={evidenceUploading}
                  >
                    <i
                      className={`bx ${
                        evidenceUploading
                          ? "bx-loader-alt bx-spin"
                          : "bx-cloud-upload"
                      }`}
                    />
                    <span>
                      <b>{evidenceUploading ? "Uploading…" : "Browse Files"}</b>
                      <small>Attach files specifically for this finding</small>
                    </span>
                  </button>
                  {linkedEvidenceFiles.length > 0 && (
                    <div className="finding-editor__files">
                      {linkedEvidenceFiles.map((file) => (
                        <article key={file.id}>
                          <i
                            className={`bx ${fileIcon(
                              file.fileName || file.name
                            )}`}
                          />
                          <span>
                            <b>{file.fileName || file.name}</b>
                            <small>
                              {Math.max(
                                1,
                                Math.round(Number(file.sizeBytes || 0) / 1024)
                              )}{" "}
                              KB
                            </small>
                          </span>
                          <button
                            type="button"
                            onClick={() => onRemoveEvidence(file.id)}
                            aria-label={`Remove ${file.fileName || file.name}`}
                          >
                            <i className="bx bx-x" />
                          </button>
                        </article>
                      ))}
                    </div>
                  )}
                  {errors.evidence && (
                    <small className="finding-editor__error">
                      {errors.evidence}
                    </small>
                  )}
                </fieldset>
              </>
            )}
          </section>
        ) : (
          <section className="finding-editor__assignment">
            <section>
              <header>
                <i className="bx bx-target-lock" />
                <span>
                  <b>Action required?</b>
                  <small>Choose the route for this finding.</small>
                </span>
              </header>
              <div className="finding-editor__choice">
                <button
                  type="button"
                  className={actionRequired ? "active" : ""}
                  onClick={() =>
                    update({
                      managementResponseRequired: "yes",
                      remediationRequired: "Yes",
                    })
                  }
                >
                  <i
                    className={`bx ${
                      actionRequired ? "bxs-check-circle" : "bx-circle"
                    }`}
                  />
                  <span>
                    <b>Yes — assign corrective action</b>
                    <small>
                      An owner responds, provides evidence, and submits for
                      review.
                    </small>
                  </span>
                </button>
                <button
                  type="button"
                  className={!actionRequired ? "active" : ""}
                  onClick={() =>
                    update({
                      managementResponseRequired: "no",
                      remediationRequired: "No",
                      responseFrom: "",
                      responseDueDate: "",
                    })
                  }
                >
                  <i
                    className={`bx ${
                      !actionRequired ? "bxs-check-circle" : "bx-circle"
                    }`}
                  />
                  <span>
                    <b>No further action</b>
                    <small>
                      Create the finding for reviewer closure and audit history.
                    </small>
                  </span>
                </button>
              </div>
              {actionRequired && (
                <div className="finding-editor__assignment-fields">
                  <label>
                    <b>
                      Assign to <em>*</em>
                    </b>
                    <select
                      value={value.responseFrom}
                      onChange={(event) =>
                        update({ responseFrom: event.target.value })
                      }
                    >
                      <option value="">Select user or role</option>
                      <optgroup label="Specific user">
                        {assignees.map((user) => (
                          <option key={user.id} value={`user:${user.id}`}>
                            {user.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Role">
                        {responseRoles.map((role) => (
                          <option key={role} value={`role:${role}`}>
                            {role}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                    {errors.responseFrom && (
                      <small className="finding-editor__error">
                        {errors.responseFrom}
                      </small>
                    )}
                  </label>
                  <label>
                    <b>
                      Due date <em>*</em>
                    </b>
                    <input
                      type="date"
                      value={value.responseDueDate}
                      onChange={(event) =>
                        update({ responseDueDate: event.target.value })
                      }
                    />
                    {errors.responseDueDate && (
                      <small className="finding-editor__error">
                        {errors.responseDueDate}
                      </small>
                    )}
                  </label>
                </div>
              )}
            </section>
            <section>
              <header>
                <i className="bx bx-file-find" />
                <span>
                  <b>Resolution details</b>
                  <small>Give the assignee enough context to act.</small>
                </span>
              </header>
              <label>
                <b>
                  Expected action
                  {normalizedRequired.has("recommendation") && <em> *</em>}
                </b>
                <textarea
                  rows="5"
                  value={value.recommendation || ""}
                  onChange={(event) =>
                    update({ recommendation: event.target.value })
                  }
                  placeholder="Describe the corrective action or expected outcome."
                />
                {errors.recommendation && (
                  <small className="finding-editor__error">
                    {errors.recommendation}
                  </small>
                )}
              </label>
            </section>
          </section>
        )}
      </ModalBody>

      <footer className="finding-editor__footer">
        <button
          type="button"
          onClick={step === 1 ? onClose : () => setStep(1)}
          disabled={saving}
        >
          {step === 1 ? "Cancel" : "Back"}
        </button>
        {step === 1 ? (
          <button
            type="button"
            className="is-primary"
            onClick={continueToAssignment}
            disabled={saving || evidenceUploading || (!hasDeficiency && cannotSave)}
          >
            {hasDeficiency
              ? "Continue to Assignment"
              : mode === "edit"
              ? "Save Changes"
              : "Create Finding"}{" "}
            <i className="bx bx-right-arrow-alt" />
          </button>
        ) : (
          <button
            type="button"
            className="is-primary"
            onClick={save}
            disabled={saving || evidenceUploading || cannotSave}
          >
            {saving
              ? "Saving…"
              : mode === "edit"
              ? "Save Changes"
              : "Create Finding"}
          </button>
        )}
      </footer>
    </Modal>
  );
};

export default FindingEditorModal;
